import React, { useEffect, useState } from "react";
import { Wallet, Plus, X, CreditCard, Zap, Loader2 } from "lucide-react";
import AddBalance from "./components/Modals/AddBalance";
import { useAppServices } from "hook/services";
import { useUserInfo } from "context/user";
import WalletLoader from "./components/Loader";
interface paymentMethodTypes {
  _id: string;
  userId: string;
  companyId: string;
  paymentMethodType: string;
  paymentMethodId: string;
  customerId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
  createdAt: string;
}
const WalletSection = () => {
  const { user } = useUserInfo();
  const AppService = useAppServices();
  const [wallet, setWallet] = useState<any>({});
  const [walletBalance, setWalletBalance] = useState(0);
  const [autoRechargeEnabled, setAutoRechargeEnabled] = useState<any>(false);
  const [threshold, setThreshold] = useState(20);
  const [rechargeAmount, setRechargeAmount] = useState(50);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<paymentMethodTypes[]>(
    []
  );

  const getPaymentMethods = async () => {
    const { response, error } = await AppService.payment_method.filter({
      query: `companyId=${user?.companyId}`,
    });
    if (response) {
      setPaymentMethods(response.data);
      setLoading(false);
    } else {
      setPaymentMethods([]);
      setLoading(false);
    }
  };
  const getWallet = async () => {
    const { response, error } = await AppService.wallet.get({
      query: `companyId=${user?.companyId}`,
    });
    if (response) {
      setWalletBalance(response.data?.balance);
      setAutoRechargeEnabled(response.data?.autoRecharge?.enabled);
      setThreshold(response.data?.autoRecharge?.threshold);
      setRechargeAmount(response.data?.autoRecharge?.amount);
      setWallet(response.data);
      setLoading(false);
    } else {
      setWalletBalance(0);
      setLoading(false);
    }
  };
  let pageLoad = true;
  const onload = () => {
    if (pageLoad) {
      getPaymentMethods();
      getWallet();
      pageLoad = false;
    }
  };

  useEffect(() => {
    onload();
  }, []);

  const enableAutoRechrage = async () => {
    const { response } = await AppService.wallet.update({
      payload: {
        _id: wallet?._id,
        autoRecharge: {
          enabled: !autoRechargeEnabled,
        },
      },
    });
    if (response) {
      setAutoRechargeEnabled(!autoRechargeEnabled);
    }
  };
  const saveAutoRechargeSettings = async () => {
    if (!wallet?._id) return;
    setSaving(true);
    const { response, error } = await AppService.wallet.update_auto_recharge({
      payload: {
        _id: wallet._id,
        autoRecharge: {
          enabled: autoRechargeEnabled,
          threshold,
          amount: rechargeAmount,
        },
      },
      toaster: true,
    });
    setSaving(false);
    if (response) {
      setSaving(false);
    } else {
      setSaving(false);
    }
  };

   if (loading) return <WalletLoader/>

  return (
    <div className="space-y-6">
      {/* Wallet Overview */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-600 rounded-2xl p-6 text-white shadow-xl overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/10 rounded-full"></div>
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-white/10 rounded-full"></div>

        <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Wallet size={20} className="opacity-90" />
              <h3 className="text-sm font-medium opacity-90">Wallet Balance</h3>
            </div>
            <p className="text-4xl md:text-5xl font-bold mb-2">
              ${walletBalance.toFixed(2)}
            </p>
            <p className="text-sm opacity-75">
              Available for all your payments and subscriptions
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <AddBalance
              handleRefresh={getWallet}
              paymentMethods={paymentMethods}
              walletId={wallet?._id}
            />
          </div>
        </div>
      </div>

      {/* Auto Recharge Settings */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-sm hover:shadow-md dark:hover:shadow-gray-900/20 transition-shadow duration-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Zap size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Auto-Recharge
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Never run out of funds with automatic top-ups
              </p>
            </div>
          </div>

          <label className="flex items-center cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoRechargeEnabled}
              onChange={enableAutoRechrage}
              className="sr-only"
            />
            <div
              className={`w-12 h-6 flex items-center rounded-full p-1 transition-all duration-300 ${
                autoRechargeEnabled
                  ? "bg-blue-600"
                  : "bg-gray-300 dark:bg-gray-600"
              }`}
            >
              <div
                className={`bg-white w-4 h-4 rounded-full shadow-lg transform transition-transform duration-300 ${
                  autoRechargeEnabled ? "translate-x-6" : ""
                }`}
              ></div>
            </div>
            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              {autoRechargeEnabled ? "Enabled" : "Disabled"}
            </span>
          </label>
        </div>

        {autoRechargeEnabled ? (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Recharge Threshold
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 font-medium">
                    $
                  </span>
                  <input
                    type="number"
                    value={threshold}
                    onChange={(e) => setThreshold(Number(e.target.value))}
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-600 dark:focus:border-blue-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm transition-all duration-200"
                    min="1"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Trigger recharge when balance drops below this amount
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Recharge Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 font-medium">
                    $
                  </span>
                  <input
                    type="number"
                    value={rechargeAmount}
                    onChange={(e) => setRechargeAmount(Number(e.target.value))}
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-600 dark:focus:border-blue-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm transition-all duration-200"
                    min="1"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Amount to add when threshold is reached
                </p>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <span className="font-semibold">Summary:</span> When your wallet
                balance drops below{" "}
                <span className="font-bold">${threshold}</span>, we'll
                automatically add{" "}
                <span className="font-bold">${rechargeAmount}</span> using your
                default payment method.
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="max-w-md mx-auto">
              <Zap
                size={48}
                className="mx-auto text-gray-300 dark:text-gray-600 mb-4"
              />
              <h4 className="text-lg font-semibold text-gray-700 dark:text-white mb-2">
                Auto-Recharge Disabled
              </h4>
              <p className="text-gray-500 dark:text-gray-400">
                Enable Auto-Recharge to automatically top up your wallet when it
                falls below your preferred threshold. Never worry about
                interrupted services again.
              </p>
            </div>
          </div>
        )}
        <div className="flex justify-end mt-4">
          <button
            onClick={saveAutoRechargeSettings}
            disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default WalletSection;
